import { ReactNode } from 'react'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { Skeleton } from '@/components/ui/skeleton'

interface FeatureFlagProps {
  /**
   * Feature flag name to check
   */
  flag: string

  /**
   * Content to render when flag is enabled
   */
  children: ReactNode

  /**
   * Content to render when flag is disabled (optional)
   */
  fallback?: ReactNode
}

/**
 * Component wrapper for feature flags
 *
 * @example
 * ```tsx
 * <FeatureFlag flag="new-reporting-ui">
 *   <NewReportingUI />
 * </FeatureFlag>
 * ```
 *
 * @example
 * ```tsx
 * <FeatureFlag flag="beta-feature" fallback={<OldFeature />}>
 *   <BetaFeature />
 * </FeatureFlag>
 * ```
 */
export function FeatureFlag({
  flag,
  children,
  fallback = null,
}: FeatureFlagProps) {
  const { isEnabled, isLoading } = useFeatureFlag()

  // This prevents the "Feature Not Available" message from flashing
  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center p-6">
        <Skeleton className="h-8 w-8 rounded-full" />
      </div>
    )
  }

  // Only check flag after loading is complete
  // This ensures we never show fallback during the loading phase
  if (isEnabled(flag)) {
    return <>{children}</>
  }

  return <>{fallback}</>
}
